"use client";

import { useState } from "react";
import DemoFooter from "@/components/preview/demo-footer";
import DemoWrapper, {
  type DemoHeight,
} from "@/components/preview/demo-wrapper";
import SliderControl from "@/components/preview/slider-control";
import { Badge } from "@/components/ui/badge";
import { ResultBadge } from "@/components/ui/result-badge";
import { SpeedToggleButton } from "@/components/ui/speed-toggle-button";
import { cn } from "@/lib/utils";

const MIN_WIDTH = 140;
const FILL_MIN_WIDTH = 220;
const MAX_WIDTH = 300;

const TITLE = "Designing interfaces that feel natural and intuitive";
const DESCRIPTION =
  "Great design is invisible. It guides users without them ever noticing.";

type Wrap = React.CSSProperties["textWrap"];

interface SampleProps {
  bad?: boolean;
  descriptionWrap: Wrap;
  fill: boolean;
  label: string;
  showGuides: boolean;
  titleWrap: Wrap;
  width: number;
}

function Sample({
  bad = false,
  descriptionWrap,
  fill,
  label,
  showGuides,
  titleWrap,
  width,
}: SampleProps) {
  const guideClassName = cn(
    "absolute w-px bg-preview-border transition-opacity will-change-transform",
    fill ? "inset-y-0" : "top-1/2 h-72 -translate-y-1/2",
    !showGuides && "opacity-0"
  );

  return (
    <div
      className={cn(
        "relative flex w-full flex-col items-center justify-center gap-4",
        fill ? "h-full" : "h-72",
        bad && "border-preview-border border-b sm:border-b-0"
      )}
    >
      <div
        className={cn(
          "relative",
          fill && "flex h-full flex-col justify-center"
        )}
        style={{ width }}
      >
        <span className={cn(guideClassName, "left-0")} />
        <span className={cn(guideClassName, "right-0")} />
        <Badge className="mb-2 whitespace-nowrap">{label}</Badge>
        <p
          className="select-none text-start font-medium text-gray-1200 text-lg leading-snug"
          style={{ textWrap: titleWrap }}
        >
          {TITLE}
        </p>
        <p
          className="mt-1 select-none text-gray-900 text-sm"
          style={{ textWrap: descriptionWrap }}
        >
          {DESCRIPTION}
        </p>
        <ResultBadge bad={bad} className="mt-2" />
      </div>
    </div>
  );
}

interface TextWrapDemoProps {
  captionText?: string;
  defaultWidth?: number;
  descriptionWrap: Wrap;
  fill?: boolean;
  height?: DemoHeight;
  label: string;
  margin?: string;
  titleWrap: Wrap;
}

export default function TextWrapDemo({
  captionText,
  defaultWidth = 220,
  descriptionWrap,
  fill = false,
  height,
  label,
  margin,
  titleWrap,
}: TextWrapDemoProps) {
  const [width, setWidth] = useState(defaultWidth);
  const [showGuides, setShowGuides] = useState(true);
  const guidesVisible = fill || showGuides;

  return (
    <DemoWrapper
      captionText={captionText}
      className="shrink-0"
      height={height}
      margin={margin}
    >
      <div
        className={cn(
          "flex w-full flex-col items-center justify-center sm:flex-row",
          fill ? "h-full" : "shrink-0"
        )}
      >
        <Sample
          bad
          descriptionWrap="wrap"
          fill={fill}
          label="wrap"
          showGuides={guidesVisible}
          titleWrap="wrap"
          width={width}
        />
        <Sample
          descriptionWrap={descriptionWrap}
          fill={fill}
          label={label}
          showGuides={guidesVisible}
          titleWrap={titleWrap}
          width={width}
        />
      </div>

      <DemoFooter className="flex-col gap-4 sm:flex-row">
        <SliderControl
          className="mb-1.5"
          label="Width"
          max={MAX_WIDTH}
          min={fill ? FILL_MIN_WIDTH : MIN_WIDTH}
          onChange={setWidth}
          step={1}
          value={width}
          valueLabel={`${width}px`}
        />
        {!fill && (
          <>
            <span className="hidden h-6 w-px shrink-0 bg-gray-400 sm:block" />
            <SpeedToggleButton
              className="h-10 w-full shrink-0 rounded-full will-change-transform sm:w-34"
              isFast={showGuides}
              labels={{ fast: "Hide guides", slow: "Show guides" }}
              onToggle={() => setShowGuides((prev) => !prev)}
            />
          </>
        )}
      </DemoFooter>
    </DemoWrapper>
  );
}
